import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpInvoiceAccountingExportProfileEnum,
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceKindEnum,
  CmpInvoiceOverdueBucketEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpInvoiceSourceFilterEnum,
  CmpManageInvoicesSortColumnEnum,
  CmpSortDirectionEnum,
  GetCmpManageInvoicesQuery,
  useApproveCmpInvoiceMutation,
  useDeleteCmpInvoiceDraftMutation,
  useDownloadCmpInvoicePdfMutation,
  useExportCmpInvoicesAccountingCsvMutation,
  useGetCmpInvoiceDetailQuery,
  useGetCmpManageInvoicesQuery,
  useRecordCmpInvoiceManualPaymentMutation,
  useRecordCmpInvoiceManualRefundMutation,
  useResendCmpInvoicePaymentRequestMutation,
  useResendCmpInvoiceReceiptMutation,
  useSendCmpInvoicePaymentRequestMutation,
  useStartCmpInvoiceCardCheckoutMutation,
  useVoidCmpInvoiceMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { commonStyles } from "../../../common/commonStyles";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
  filterControlBorderColor,
} from "../../../common/dashboard/FilterBar";
import {
  ManagePageHeaderOverride,
  ManagePageShell,
} from "../../../common/dashboard/ManagePageShell";
import {
  buildRowActions,
  RowActionMatrix,
  RowActionMenu,
  RowActionsTrigger,
} from "../../../common/dashboard/RowActionMenu";
import { StatusBadge } from "../../../common/dashboard/StatusBadge";
import CalendarDateInput from "../../MyChamber/ChamberSettings/common/CalendarDateInput";
import {
  dateFormatter,
  formatCurrency,
  formatDate,
  getRecordedPaymentMethodLabel,
} from "../invoiceFormatting";
import { downloadBase64File, downloadTextFile } from "../invoiceFileDownloads";
import {
  InvoicePreviewModal,
  InvoiceSuccessDialog,
  ProcessPaymentModal,
  RecordInvoicePaymentInput,
} from "./ManageInvoiceActionModals";

type SortDirection = "asc" | "desc";
type InvoiceRow = GetCmpManageInvoicesQuery["getCmpManageInvoices"]["rows"][number];

type ColumnKey =
  | "invoiceNumber"
  | "customerName"
  | "type"
  | "issueDate"
  | "dueDate"
  | "amount"
  | "balance"
  | "status"
  | "paymentMethodLabel";

// Full DataTable column-key set: the 9 sortable ColumnKey columns plus the
// decorative leading checkbox and the non-sortable Actions column.
type InvoiceTableColumnKey = ColumnKey | "select" | "actions";

type StatusFilter = "all" | "overdue" | CmpInvoiceDisplayStateEnum;
type DangerDialogKind = "reverse" | "cancel" | "delete";
type ReportingPeriod = "yearToDate" | "monthly";

const { cpaGold, cpaGoldDark } = commonStyles;
const maxManageInvoiceRows = 10000;
const shouldMarkCmpInvoiceModuleTestData = process.env.NODE_ENV === "development";

// Page-specific variant of the shared FilterBar filterFieldSx: this page uses
// minHeight:56 where the shared default uses height:56 — passed via the
// fieldSx escape hatch to keep the rendered CSS identical (B4 audit drift b).
const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "6px",
    minHeight: 56,
    "& fieldset": {
      borderColor: filterControlBorderColor,
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderColor: cpaGold,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
};

const headerButtonSx = {
  backgroundColor: cpaGold,
  color: "#fff",
  fontSize: 13,
  fontWeight: 800,
  minHeight: 40,
  px: 2.75,
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: 1,
    "& svg": { fontSize: 18 },
  },
  "&:hover": { backgroundColor: cpaGoldDark },
};

// This page's idle filter-button label is #8F969E where the shared FilterBar
// filterButtonSx uses #6B7280; FilterMenuButton merges its sx prop over both
// the active and inactive base, so each button passes
// sx={isActive ? undefined : idleFilterButtonLabelSx} — conditional so the
// active #111827 label (shared activeFilterButtonSx, byte-identical to the
// deleted local INV-MANAGE-003 variant) is never clobbered.
const idleFilterButtonLabelSx = { color: "#8F969E" };

const exportButtonSx = {
  ...headerButtonSx,
  borderRadius: "6px",
  fontSize: 14,
  minHeight: 44,
  minWidth: 220,
  px: 2.75,
};

const tableActionButtonSx = {
  borderColor: "#E5E7EB",
  borderRadius: "6px",
  color: "#111827",
  fontSize: 14,
  fontWeight: 800,
  height: 44,
  minWidth: 126,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "white",
    borderColor: "#CBD5E1",
  },
};

const tableHeaderCellSx = {
  color: "#111827",
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0,
  whiteSpace: "nowrap",
};

const tableCellSx = {
  color: "#4B5563",
  fontSize: 15,
  lineHeight: 1.35,
  minWidth: 0,
  overflowWrap: "anywhere",
  verticalAlign: "middle",
};

const actionMenuPaperSx = {
  borderRadius: "6px",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.14)",
  overflow: "hidden",
  py: 0.75,
  width: 330,
};

const actionMenuItemSx = {
  color: "#374151",
  fontSize: 18,
  fontWeight: 600,
  gap: 1.5,
  lineHeight: 1.3,
  minHeight: 56,
  px: 2.5,
  py: 1.25,
  "& .MuiSvgIcon-root": {
    color: "#9CA3AF",
    fontSize: 24,
    flexShrink: 0,
  },
};

// DELTA over actionMenuItemSx: RowActionMenu merges [itemSx, destructiveItemSx]
// as an sx array, so this carries only the red overrides. It must stay this
// page's #E1262D text / #F31723 icon pair — NEVER the shared
// rowActionDestructiveItemSx #DC2626 default (visible regression).
const destructiveMenuItemSx = {
  color: "#E1262D",
  fontWeight: 800,
  "& .MuiSvgIcon-root": {
    color: "#F31723",
    fontSize: 24,
    flexShrink: 0,
  },
};

// Verbatim copy of the Table sx this page rendered before the DataTable
// adoption (px 1.25 / py 3.75 body cells, #F8FAFC head cells).
const invoiceTableSx = {
  minWidth: 1120,
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableCell-root": {
    borderColor: "#E5E7EB",
    overflowWrap: "anywhere",
    px: 1.25,
    py: 3.75,
    verticalAlign: "middle",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "#F8FAFC",
    py: 2.5,
    whiteSpace: "nowrap",
  },
};

const invoiceTypeFilterOptions: Array<{
  value: "all" | CmpInvoiceSourceFilterEnum;
  label: string;
}> = [
  { value: "all", label: "All invoice types" },
  { value: CmpInvoiceSourceFilterEnum.Membership, label: "Membership" },
  { value: CmpInvoiceSourceFilterEnum.EventTicket, label: "Event Ticket" },
  { value: CmpInvoiceSourceFilterEnum.EventSponsorship, label: "Event Sponsorship" },
  { value: CmpInvoiceSourceFilterEnum.Advertising, label: "Advertising" },
  { value: CmpInvoiceSourceFilterEnum.Custom, label: "Custom / Standalone" },
];

const statusFilterOptions: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: CmpInvoiceDisplayStateEnum.Draft, label: "Draft" },
  { value: CmpInvoiceDisplayStateEnum.NotPaid, label: "Not paid" },
  { value: "overdue", label: "Overdue" },
  { value: CmpInvoiceDisplayStateEnum.Paid, label: "Paid" },
  { value: CmpInvoiceDisplayStateEnum.Refunded, label: "Refunded" },
  { value: CmpInvoiceDisplayStateEnum.Voided, label: "Voided" },
  { value: CmpInvoiceDisplayStateEnum.Waived, label: "Waived" },
  { value: CmpInvoiceDisplayStateEnum.PaymentFailed, label: "Failed" },
  { value: CmpInvoiceDisplayStateEnum.PaymentAbandoned, label: "Abandoned" },
  { value: CmpInvoiceDisplayStateEnum.PaymentExpired, label: "Expired" },
  { value: CmpInvoiceDisplayStateEnum.ActionRequired, label: "Action required" },
];

const sortColumnMap: Record<ColumnKey, CmpManageInvoicesSortColumnEnum> = {
  invoiceNumber: CmpManageInvoicesSortColumnEnum.InvoiceNumber,
  customerName: CmpManageInvoicesSortColumnEnum.CustomerName,
  type: CmpManageInvoicesSortColumnEnum.InvoiceKind,
  issueDate: CmpManageInvoicesSortColumnEnum.IssueDate,
  dueDate: CmpManageInvoicesSortColumnEnum.DueDate,
  amount: CmpManageInvoicesSortColumnEnum.AmountDue,
  balance: CmpManageInvoicesSortColumnEnum.AmountDue,
  status: CmpManageInvoicesSortColumnEnum.DisplayState,
  paymentMethodLabel: CmpManageInvoicesSortColumnEnum.PaymentMethod,
};

const getUpdatePath = (invoiceId: string) =>
  RoutePaths.chamberAdmin_invoicing_update.replace(":invoiceId", invoiceId);

const getDisplayStateFilters = (statusFilter: StatusFilter) => {
  if (statusFilter === "all" || statusFilter === "overdue") {
    return undefined;
  }

  return [statusFilter];
};

const getOverdueBucketFilters = (statusFilter: StatusFilter) => {
  if (statusFilter === "overdue") {
    return [
      CmpInvoiceOverdueBucketEnum.OverdueUnder_60,
      CmpInvoiceOverdueBucketEnum.Overdue_60_89,
      CmpInvoiceOverdueBucketEnum.Overdue_90_119,
      CmpInvoiceOverdueBucketEnum.Overdue_120Plus,
    ];
  }

  return undefined;
};

const normalizeSearchText = (value?: string | null) =>
  (value || "").trim().toLowerCase();

const getDateInputStartUtcMilli = (value: string) =>
  value ? new Date(`${value}T00:00:00`).getTime() : null;

const getDateInputEndUtcMilli = (value: string) =>
  value ? new Date(`${value}T23:59:59.999`).getTime() : null;

const formatDateInputLabel = (value: string) =>
  value ? dateFormatter.format(new Date(`${value}T00:00:00`)) : "";

const getInvoiceDateFilterLabel = ({
  invoiceDateFromFilter,
  invoiceDateToFilter,
}: {
  invoiceDateFromFilter: string;
  invoiceDateToFilter: string;
}) => {
  if (invoiceDateFromFilter && invoiceDateToFilter) {
    return `${formatDateInputLabel(invoiceDateFromFilter)} - ${formatDateInputLabel(invoiceDateToFilter)}`;
  }

  if (invoiceDateFromFilter) {
    return `From ${formatDateInputLabel(invoiceDateFromFilter)}`;
  }

  if (invoiceDateToFilter) {
    return `Through ${formatDateInputLabel(invoiceDateToFilter)}`;
  }

  return "Filter by Invoice Date";
};

const getInvoiceSearchText = (row: InvoiceRow) =>
  normalizeSearchText(
    [
      row.invoiceNumber,
      row.customerName,
      row.sourceLabel,
      row.paymentMethodLabel,
      row.actualEmailRecipient,
      row.intendedEmailRecipient,
    ].join(" ")
  );

const rowMatchesSourceFilter = (
  row: InvoiceRow,
  sourceFilter: (typeof invoiceTypeFilterOptions)[number]["value"]
) => {
  if (sourceFilter === "all") {
    return true;
  }

  if (sourceFilter === CmpInvoiceSourceFilterEnum.Membership) {
    return [CmpInvoiceKindEnum.Application, CmpInvoiceKindEnum.Renewal].includes(
      row.invoiceKind
    );
  }

  if (sourceFilter === CmpInvoiceSourceFilterEnum.EventTicket) {
    return row.invoiceKind === CmpInvoiceKindEnum.EventRegistration;
  }

  if (sourceFilter === CmpInvoiceSourceFilterEnum.EventSponsorship) {
    return row.invoiceKind === CmpInvoiceKindEnum.EventSponsorship;
  }

  if (sourceFilter === CmpInvoiceSourceFilterEnum.Advertising) {
    return row.invoiceKind === CmpInvoiceKindEnum.Advertising;
  }

  if (sourceFilter === CmpInvoiceSourceFilterEnum.Custom) {
    return row.invoiceKind === CmpInvoiceKindEnum.Custom;
  }

  return true;
};

const rowMatchesStatusFilter = (
  row: InvoiceRow,
  statusFilter: StatusFilter
) => {
  if (statusFilter === "all") {
    return true;
  }

  if (statusFilter === "overdue") {
    return ![
      CmpInvoiceOverdueBucketEnum.NotApplicable,
      CmpInvoiceOverdueBucketEnum.Current,
    ].includes(row.overdueBucket);
  }

  return row.displayState === statusFilter;
};

const rowMatchesInvoiceDateRange = (
  row: InvoiceRow,
  invoiceDateFromFilter: string,
  invoiceDateToFilter: string
) => {
  if (!invoiceDateFromFilter && !invoiceDateToFilter) {
    return true;
  }

  if (!row.issuedAtUtcMilli) {
    return false;
  }

  const fromUtcMilli = getDateInputStartUtcMilli(invoiceDateFromFilter);
  const toUtcMilli = getDateInputEndUtcMilli(invoiceDateToFilter);
  const startUtcMilli =
    fromUtcMilli !== null && toUtcMilli !== null
      ? Math.min(fromUtcMilli, toUtcMilli)
      : fromUtcMilli;
  const endUtcMilli =
    fromUtcMilli !== null && toUtcMilli !== null
      ? Math.max(fromUtcMilli, toUtcMilli)
      : toUtcMilli;

  if (startUtcMilli !== null && row.issuedAtUtcMilli < startUtcMilli) {
    return false;
  }

  if (endUtcMilli !== null && row.issuedAtUtcMilli > endUtcMilli) {
    return false;
  }

  return true;
};

const dateRangeMenuPaperSx = {
  borderRadius: "8px",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
  mt: 0.75,
  // overflow visible so the CalendarDateInput dropdown can extend past the menu
  // surface instead of being clipped (mirrors the ManageMembers renewal filter).
  overflow: "visible",
  p: 2,
  width: 320,
};

const filterInvoiceRows = ({
  invoiceDateFromFilter,
  invoiceDateToFilter,
  invoiceNumberSearchQuery,
  memberSearchQuery,
  paymentMethodFilter,
  rows,
  statusFilter,
  typeFilter,
}: {
  invoiceDateFromFilter: string;
  invoiceDateToFilter: string;
  invoiceNumberSearchQuery: string;
  memberSearchQuery: string;
  paymentMethodFilter: string;
  rows: InvoiceRow[];
  statusFilter: StatusFilter;
  typeFilter: (typeof invoiceTypeFilterOptions)[number]["value"];
}) => {
  const invoiceNumberSearch = normalizeSearchText(invoiceNumberSearchQuery);
  const memberSearch = normalizeSearchText(memberSearchQuery);

  return rows.filter((row) => {
    if (
      invoiceNumberSearch &&
      !normalizeSearchText(row.invoiceNumber).includes(invoiceNumberSearch)
    ) {
      return false;
    }

    if (memberSearch && !getInvoiceSearchText(row).includes(memberSearch)) {
      return false;
    }

    if (!rowMatchesSourceFilter(row, typeFilter)) {
      return false;
    }

    if (!rowMatchesStatusFilter(row, statusFilter)) {
      return false;
    }

    if (
      !rowMatchesInvoiceDateRange(
        row,
        invoiceDateFromFilter,
        invoiceDateToFilter
      )
    ) {
      return false;
    }

    if (!rowHasPaymentMethodFilter(row, paymentMethodFilter)) {
      return false;
    }

    return true;
  });
};

const sortInvoiceRows = ({
  rows,
  sortColumn,
  sortDirection,
}: {
  rows: InvoiceRow[];
  sortColumn: ColumnKey;
  sortDirection: SortDirection;
}) => {
  const direction = sortDirection === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    if (sortColumn === "invoiceNumber") {
      return a.invoiceNumber.localeCompare(b.invoiceNumber) * direction;
    }

    if (sortColumn === "customerName") {
      return a.customerName.localeCompare(b.customerName) * direction;
    }

    if (sortColumn === "type") {
      return a.sourceLabel.localeCompare(b.sourceLabel) * direction;
    }

    if (sortColumn === "dueDate") {
      return ((a.dueAtUtcMilli || 0) - (b.dueAtUtcMilli || 0)) * direction;
    }

    if (sortColumn === "amount") {
      return (a.totalAmountInCents - b.totalAmountInCents) * direction;
    }

    if (sortColumn === "balance") {
      return (a.balanceDueInCents - b.balanceDueInCents) * direction;
    }

    if (sortColumn === "status") {
      return a.displayState.localeCompare(b.displayState) * direction;
    }

    if (sortColumn === "paymentMethodLabel") {
      return a.paymentMethodLabel.localeCompare(b.paymentMethodLabel) * direction;
    }

    return (a.issuedAtUtcMilli - b.issuedAtUtcMilli) * direction;
  });
};

const getInvoiceRowsSummary = (rows: InvoiceRow[]) => ({
  totalAmountDueInCents: rows.reduce(
    (sum, row) => sum + row.amountDueInCents,
    0
  ),
  totalInvoicedAmountInCents: rows.reduce(
    (sum, row) => sum + row.totalAmountInCents,
    0
  ),
  totalPaidAmountInCents: rows.reduce(
    (sum, row) => sum + row.totalPaidInCents,
    0
  ),
});

const getReportingPeriodRows = (
  rows: InvoiceRow[],
  period: ReportingPeriod
) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  return rows.filter((row) => {
    const rowDateValue = row.issuedAtUtcMilli || row.dueAtUtcMilli;

    if (!rowDateValue) {
      return false;
    }

    const rowDate = new Date(rowDateValue);

    if (period === "monthly") {
      return (
        rowDate.getFullYear() === currentYear &&
        rowDate.getMonth() === currentMonth
      );
    }

    return rowDate.getFullYear() === currentYear && rowDate.getTime() <= now.getTime();
  });
};

const getDisplayStateLabel = (state: CmpInvoiceDisplayStateEnum) => {
  switch (state) {
    case CmpInvoiceDisplayStateEnum.Draft:
      return "Draft";
    case CmpInvoiceDisplayStateEnum.Paid:
      return "Paid";
    case CmpInvoiceDisplayStateEnum.Refunded:
      return "Refunded";
    case CmpInvoiceDisplayStateEnum.Voided:
      return "Voided";
    case CmpInvoiceDisplayStateEnum.Waived:
      return "Waived";
    case CmpInvoiceDisplayStateEnum.PaymentFailed:
      return "Payment failed";
    case CmpInvoiceDisplayStateEnum.PaymentAbandoned:
      return "Abandoned";
    case CmpInvoiceDisplayStateEnum.PaymentExpired:
      return "Expired";
    case CmpInvoiceDisplayStateEnum.ActionRequired:
      return "Action required";
    case CmpInvoiceDisplayStateEnum.NotPaid:
    default:
      return "Not paid";
  }
};

const getStatusTextClassName = (state: CmpInvoiceDisplayStateEnum) => {
  switch (state) {
    case CmpInvoiceDisplayStateEnum.Draft:
      return "text-gray-500";
    case CmpInvoiceDisplayStateEnum.Paid:
      return "text-green-700";
    case CmpInvoiceDisplayStateEnum.Refunded:
    case CmpInvoiceDisplayStateEnum.Waived:
      return "text-sky-700";
    case CmpInvoiceDisplayStateEnum.Voided:
      return "text-gray-600";
    case CmpInvoiceDisplayStateEnum.PaymentFailed:
    case CmpInvoiceDisplayStateEnum.PaymentAbandoned:
    case CmpInvoiceDisplayStateEnum.PaymentExpired:
    case CmpInvoiceDisplayStateEnum.ActionRequired:
      return "text-red-600";
    case CmpInvoiceDisplayStateEnum.NotPaid:
    default:
      return "text-cpaGold";
  }
};

const getRowStatusCopy = (row: InvoiceRow) => {
  if (row.displayState === CmpInvoiceDisplayStateEnum.Draft) {
    return {
      className: "text-gray-500",
      primary: "Draft Invoice",
      secondary: "",
    };
  }

  if (row.displayState === CmpInvoiceDisplayStateEnum.NotPaid) {
    if (row.daysPastDue && row.daysPastDue > 0) {
      return {
        className: "text-[#F31723]",
        primary: "Overdue",
        secondary: `${Math.round(row.daysPastDue)} Days`,
      };
    }

    return {
      className: "text-[#1684FF]",
      primary: "Outstanding",
      secondary: row.paymentPlan?.enabled
        ? `${row.paymentPlan.paidInstallments}/${row.paymentPlan.totalInstallments} Installments`
        : "",
    };
  }

  if (row.displayState === CmpInvoiceDisplayStateEnum.Paid) {
    return {
      className: "text-green-700",
      primary: "Paid",
      secondary: getRecordedPaymentMethodLabel(row.paymentMethodLabel),
    };
  }

  if (row.displayState === CmpInvoiceDisplayStateEnum.Refunded) {
    return {
      className: "text-gray-950",
      primary: "Refunded",
      secondary: "Void Invoice",
    };
  }

  if (row.displayState === CmpInvoiceDisplayStateEnum.Voided) {
    return {
      className: "text-gray-600",
      primary: "Voided",
      secondary: "",
    };
  }

  return {
    className: getStatusTextClassName(row.displayState),
    primary: getDisplayStateLabel(row.displayState),
    secondary: "",
  };
};

const getBillingMethodLines = (row: InvoiceRow) => {
  if (row.paymentRules.ruleKind === CmpInvoicePaymentRuleKindEnum.MonthlyCreditCardOnly) {
    return ["Monthly"];
  }

  if (
    row.paymentRules.ruleKind ===
    CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly
  ) {
    return ["Chamber Group", "Insurance"];
  }

  const recordedPaymentMethodLabel = getRecordedPaymentMethodLabel(row.paymentMethodLabel);

  if (recordedPaymentMethodLabel) {
    return [recordedPaymentMethodLabel];
  }

  if (row.paymentRules.creditCardAllowed && !row.paymentRules.manualPaymentAllowed) {
    return ["Credit Card"];
  }

  if (row.paymentRules.manualPaymentAllowed) {
    return row.paymentRules.allowedManualPaymentMethodLabels.slice(0, 2);
  }

  return ["-"];
};

// INV-MANAGE-002: the Billing Method filter must match the value the column
// actually shows (getBillingMethodLines), not the chamber's allowed-methods
// fan-out. Deriving the filter from the same displayed value fixes two bugs:
// selecting e.g. "Cheque" no longer matches every invoice that merely *allows*
// cheque, and rows displayed as "Monthly" / "Chamber Group / Insurance" become
// filterable (they previously had no matching option). The multi-line display
// (["Chamber Group", "Insurance"]) is joined into one canonical label so the
// option list and the row value compare as a single string.
const getBillingMethodFilterLabel = (row: InvoiceRow) =>
  getBillingMethodLines(row).join(" / ");

const isBillingMethodFilterOption = (value: string) => value !== "-";

const getPaymentMethodOptions = (rows: InvoiceRow[]) =>
  [
    "all",
    ...Array.from(
      new Set(
        rows
          .map((row) => getBillingMethodFilterLabel(row))
          .filter(isBillingMethodFilterOption)
      )
    ),
  ];

const rowHasPaymentMethodFilter = (row: InvoiceRow, paymentMethodFilter: string) => {
  if (paymentMethodFilter === "all") {
    return true;
  }

  return getBillingMethodFilterLabel(row) === paymentMethodFilter;
};

const getActionAvailability = (
  row: InvoiceRow,
  action: CmpInvoiceActionTypeEnum
) =>
  row.availableActions.find((availableAction) => availableAction.action === action);

const canUseAction = (row: InvoiceRow, action: CmpInvoiceActionTypeEnum) =>
  !!getActionAvailability(row, action)?.available;

// Drafts and reversed/cancelled invoices are not receivables, so their Balance
// Owing reads as an en-dash rather than "$0.00" (matching the design).
const getBalanceOwingDisplay = (row: InvoiceRow) => {
  if (
    row.displayState === CmpInvoiceDisplayStateEnum.Draft ||
    row.displayState === CmpInvoiceDisplayStateEnum.Refunded ||
    row.displayState === CmpInvoiceDisplayStateEnum.Voided
  ) {
    return "–";
  }

  return row.balanceDueInCents > 0
    ? formatCurrency(row.balanceDueInCents)
    : "$0.00";
};

// INV-TABLE-001: "Edit Draft Invoice" must only appear for drafts. The backend
// Edit action stays available for any unpaid custom/advertising invoice (it also
// feeds UpdateInvoice's void-and-reissue flow), so we add an explicit
// front-end Draft gate here rather than relying on the backend availability.
const canEditCustomInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.Draft &&
  (row.invoiceKind === CmpInvoiceKindEnum.Custom ||
    row.invoiceKind === CmpInvoiceKindEnum.Advertising) &&
  canUseAction(row, CmpInvoiceActionTypeEnum.Edit);

const canApplyPaymentToInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.NotPaid &&
  row.amountDueInCents > 0 &&
  (row.paymentRules.creditCardAllowed ||
    (row.paymentRules.manualPaymentAllowed &&
      canUseAction(row, CmpInvoiceActionTypeEnum.RecordManualPayment)));

const canReversePaidInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.Paid &&
  canUseAction(row, CmpInvoiceActionTypeEnum.Refund);

const canCancelUnpaidInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.NotPaid &&
  canUseAction(row, CmpInvoiceActionTypeEnum.VoidReissue);

const isDraftInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.Draft;

const getNextInstallmentId = (row: InvoiceRow) =>
  row.paymentPlan?.nextInstallmentId || undefined;

const getExportProfileLabel = (profile: CmpInvoiceAccountingExportProfileEnum) => {
  switch (profile) {
    case CmpInvoiceAccountingExportProfileEnum.QuickbooksOnline:
      return "QuickBooks CSV";
    case CmpInvoiceAccountingExportProfileEnum.SageBusinessCloud:
      return "Sage CSV";
    case CmpInvoiceAccountingExportProfileEnum.GenericCsv:
    default:
      return "Canonical CSV";
  }
};

const getStripeReturnUrl = () =>
  `${window.location.origin}${RoutePaths.chamberAdmin_invoicing_manage}`;

const getDangerDialogConfig = (kind: DangerDialogKind) => {
  switch (kind) {
    case "reverse":
      return {
        actionLabel: "YES",
        Icon: ReplayIcon,
        title: "Reverse Paid Invoice",
        verb: "refund the paid invoice",
      };
    case "cancel":
      return {
        actionLabel: "YES",
        Icon: CancelOutlinedIcon,
        title: "Cancel Unpaid Invoice",
        verb: "cancel the unpaid invoice",
      };
    case "delete":
    default:
      return {
        actionLabel: "DELETE",
        Icon: DeleteOutlineIcon,
        title: "Delete Draft Invoice",
        verb: "delete the draft invoice",
      };
  }
};

// ---------------------------------------------------------------------------
// Row Actions menu config (shared RowActionMenu adoption, B4). The matrix,
// labels, icons and gates reproduce the former per-row <Menu> verbatim:
// same order, same single divider before the (status-exclusive) destructive
// item, same disabled semantics.
// ---------------------------------------------------------------------------

type InvoiceMenuStatus =
  | "draft"
  | "outstanding"
  | "paid"
  | "refunded"
  | "voided"
  | "other";

type InvoiceRowActionKey =
  | "preview"
  | "editDraft"
  | "printPdf"
  | "applyPayment"
  | "emailInvoice"
  | "reversePaid"
  | "cancelUnpaid"
  | "deleteDraft";

const getInvoiceMenuStatus = (row: InvoiceRow): InvoiceMenuStatus => {
  switch (row.displayState) {
    case CmpInvoiceDisplayStateEnum.Draft:
      return "draft";
    case CmpInvoiceDisplayStateEnum.NotPaid:
      return "outstanding";
    case CmpInvoiceDisplayStateEnum.Paid:
      return "paid";
    case CmpInvoiceDisplayStateEnum.Refunded:
      return "refunded";
    case CmpInvoiceDisplayStateEnum.Voided:
      return "voided";
    // Waived / PaymentFailed / PaymentAbandoned / PaymentExpired /
    // ActionRequired all render the base Preview / Print / Email menu.
    default:
      return "other";
  }
};

const invoiceRowActionMatrix: RowActionMatrix<
  InvoiceMenuStatus,
  InvoiceRowActionKey
> = {
  draft: ["preview", "editDraft", "printPdf", "emailInvoice", "deleteDraft"],
  outstanding: [
    "preview",
    "printPdf",
    "applyPayment",
    "emailInvoice",
    "cancelUnpaid",
  ],
  paid: ["preview", "printPdf", "emailInvoice", "reversePaid"],
  refunded: ["preview", "printPdf", "emailInvoice"],
  voided: ["preview", "printPdf", "emailInvoice"],
  other: ["preview", "printPdf", "emailInvoice"],
};

const invoiceRowActionLabels: Record<InvoiceRowActionKey, string> = {
  preview: "Preview Invoice",
  editDraft: "Edit Draft Invoice",
  printPdf: "Print Invoice PDF",
  applyPayment: "Apply Payment",
  emailInvoice: "Email Invoice",
  reversePaid: "Reverse Paid Invoice",
  cancelUnpaid: "Cancel Unpaid Invoice",
  deleteDraft: "Delete Draft",
};

const invoiceRowActionIcons: Record<InvoiceRowActionKey, React.ElementType> = {
  preview: VisibilityOutlinedIcon,
  editDraft: EditOutlinedIcon,
  printPdf: PictureAsPdfOutlinedIcon,
  applyPayment: PaymentsOutlinedIcon,
  emailInvoice: EmailOutlinedIcon,
  reversePaid: ReplayIcon,
  cancelUnpaid: CancelOutlinedIcon,
  deleteDraft: DeleteOutlineIcon,
};

// The three destructive actions are mutually exclusive by display state
// (reverse=Paid, cancel=NotPaid, delete=Draft), so dividerBefore on all three
// reproduces the former single <Divider/> before the destructive block.
const invoiceDestructiveRowActions: ReadonlySet<InvoiceRowActionKey> =
  new Set<InvoiceRowActionKey>(["reversePaid", "cancelUnpaid", "deleteDraft"]);

const ManageInvoices = () => {
  const navigate = useNavigate();

  // The gold "+ Create New Invoice" header action (formerly PageContainer's
  // isManageInvoicesPage default, now owned solely by this page). Passed
  // EXPLICITLY through the header override. Built inside the
  // component with useMemo (NOT at module scope): enumStrings imports this
  // page for the route registry, so evaluating RoutePaths at module init hits
  // the circular-import TDZ and white-screens the whole app on cold load.
  // useMemo([]) keeps the object referentially stable for ManagePageShell.
  const manageInvoicesHeaderOverride: ManagePageHeaderOverride = useMemo(
    () => ({
      titleClassName: "text-[34px] font-extrabold leading-tight text-black",
      descriptionClassName:
        "mt-3 text-lg leading-7 text-gray-500 whitespace-pre-line",
      actions: (
        <Button
          component={RouterLink}
          to={RoutePaths.chamberAdmin_invoicing_create}
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            alignSelf: { xs: "flex-start", sm: "center" },
            backgroundColor: commonStyles.cpaGold,
            borderRadius: "6px",
            color: "white",
            fontSize: 16,
            fontWeight: 800,
            minHeight: 52,
            px: 3,
            textTransform: "none",
            whiteSpace: "nowrap",
            "& .MuiButton-startIcon": {
              mr: 1,
              "& svg": { fontSize: 22 },
            },
            "&:hover": { backgroundColor: commonStyles.cpaGoldDark },
          }}
        >
          Create New Invoice
        </Button>
      ),
    }),
    []
  );
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [invoiceNumberSearchQuery, setInvoiceNumberSearchQuery] = useState("");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] =
    useState<(typeof invoiceTypeFilterOptions)[number]["value"]>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [invoiceDateFromFilter, setInvoiceDateFromFilter] = useState("");
  const [invoiceDateToFilter, setInvoiceDateToFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [dateMenuAnchor, setDateMenuAnchor] = useState<null | HTMLElement>(null);
  const [paymentMenuAnchor, setPaymentMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortColumn, setSortColumn] = useState<ColumnKey>("issueDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [previewDialogInvoice, setPreviewDialogInvoice] =
    useState<InvoiceRow | null>(null);
  const [paymentDialogInvoice, setPaymentDialogInvoice] =
    useState<InvoiceRow | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuInvoice, setActionMenuInvoice] = useState<InvoiceRow | null>(null);
  const [dangerDialog, setDangerDialog] = useState<{
    invoice: InvoiceRow;
    kind: DangerDialogKind;
  } | null>(null);
  const [lastAction, setLastAction] = useState<string | null>(null);
  // INV-PREVIEW-003: message for the green-check success dialog shown after a
  // successful send (above the fullscreen preview dialog).
  const [sendSuccessMessage, setSendSuccessMessage] = useState<string | null>(
    null
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [analyticsPeriod, setAnalyticsPeriod] =
    useState<ReportingPeriod>("yearToDate");
  const [agingPeriod, setAgingPeriod] =
    useState<ReportingPeriod>("yearToDate");

  const stripeStatus = new URLSearchParams(window.location.search).get(
    "cmpInvoiceStripeStatus"
  );

  const [
    sendCmpInvoicePaymentRequest,
    { loading: sendingPaymentRequest },
  ] = useSendCmpInvoicePaymentRequestMutation();
  const [
    resendCmpInvoicePaymentRequest,
    { loading: resendingPaymentRequest },
  ] = useResendCmpInvoicePaymentRequestMutation();
  const [downloadCmpInvoicePdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation();
  const [exportCmpInvoicesAccountingCsv, { loading: exportingCsv }] =
    useExportCmpInvoicesAccountingCsvMutation();
  const [recordCmpInvoiceManualPayment, { loading: recordingManualPayment }] =
    useRecordCmpInvoiceManualPaymentMutation();
  const [startCmpInvoiceCardCheckout, { loading: startingCardCheckout }] =
    useStartCmpInvoiceCardCheckoutMutation();
  const [approveCmpInvoice, { loading: approvingInvoice }] =
    useApproveCmpInvoiceMutation();
  const [deleteCmpInvoiceDraft, { loading: deletingDraft }] =
    useDeleteCmpInvoiceDraftMutation();
  const [voidCmpInvoice, { loading: voidingInvoice }] =
    useVoidCmpInvoiceMutation();
  const [recordCmpInvoiceManualRefund, { loading: recordingRefund }] =
    useRecordCmpInvoiceManualRefundMutation();
  const [resendCmpInvoiceReceipt, { loading: resendingReceipt }] =
    useResendCmpInvoiceReceiptMutation();

  const searchTerm = useMemo(
    () =>
      [invoiceNumberSearchQuery.trim(), memberSearchQuery.trim()]
        .filter(Boolean)
        .join(" "),
    [invoiceNumberSearchQuery, memberSearchQuery]
  );

  const {
    data: manageInvoicesData,
    loading: manageInvoicesLoading,
    error: manageInvoicesError,
    refetch,
  } = useGetCmpManageInvoicesQuery({
    variables: {
      input: {
        chamberId,
        sortColumn: CmpManageInvoicesSortColumnEnum.IssueDate,
        sortDirection: CmpSortDirectionEnum.Desc,
        limit: maxManageInvoiceRows,
        offset: 0,
      },
    },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const selectedActionInvoice = previewDialogInvoice || paymentDialogInvoice;
  const {
    data: invoiceDetailData,
    loading: invoiceDetailLoading,
    error: invoiceDetailError,
    refetch: refetchInvoiceDetail,
  } = useGetCmpInvoiceDetailQuery({
    variables: {
      input: {
        chamberId,
        invoiceId: selectedActionInvoice?.id || "",
      },
    },
    skip: !chamberId || !selectedActionInvoice?.id,
    fetchPolicy: "cache-and-network",
  });
  const selectedInvoiceDetail = invoiceDetailData?.getCmpInvoiceDetail || null;

  const rows = useMemo(
    () => manageInvoicesData?.getCmpManageInvoices.rows || [],
    [manageInvoicesData?.getCmpManageInvoices.rows]
  );
  const visibleRows = useMemo(
    () =>
      sortInvoiceRows({
        rows: filterInvoiceRows({
          invoiceDateFromFilter,
          invoiceDateToFilter,
          invoiceNumberSearchQuery,
          memberSearchQuery,
          paymentMethodFilter,
          rows,
          statusFilter,
          typeFilter,
        }),
        sortColumn,
        sortDirection,
      }),
    [
      invoiceDateFromFilter,
      invoiceDateToFilter,
      invoiceNumberSearchQuery,
      memberSearchQuery,
      paymentMethodFilter,
      rows,
      sortColumn,
      sortDirection,
      statusFilter,
      typeFilter,
    ]
  );
  const paymentMethodOptions = useMemo(() => getPaymentMethodOptions(rows), [rows]);

  useEffect(() => {
    if (
      paymentMethodFilter !== "all" &&
      !paymentMethodOptions.includes(paymentMethodFilter)
    ) {
      setPaymentMethodFilter("all");
    }
  }, [paymentMethodFilter, paymentMethodOptions]);

  // Drafts are not approved receivables, so they never count toward analytics
  // (invoiced / paid / outstanding / overdue revenue or the aging summary).
  const revenueRows = useMemo(
    () =>
      visibleRows.filter(
        (row) => row.displayState !== CmpInvoiceDisplayStateEnum.Draft
      ),
    [visibleRows]
  );
  const analyticsRows = useMemo(
    () => getReportingPeriodRows(revenueRows, analyticsPeriod),
    [analyticsPeriod, revenueRows]
  );
  const agingRows = useMemo(
    () => getReportingPeriodRows(revenueRows, agingPeriod),
    [agingPeriod, revenueRows]
  );
  const summary = useMemo(
    () => getInvoiceRowsSummary(analyticsRows),
    [analyticsRows]
  );
  const actionLoading =
    sendingPaymentRequest ||
    resendingPaymentRequest ||
    downloadingPdf ||
    exportingCsv ||
    recordingManualPayment ||
    startingCardCheckout ||
    approvingInvoice ||
    deletingDraft ||
    voidingInvoice ||
    recordingRefund ||
    resendingReceipt;

  const handleRequestSort = (column: ColumnKey) => {
    const isAscending = sortColumn === column && sortDirection === "asc";
    setSortColumn(column);
    setSortDirection(isAscending ? "desc" : "asc");
  };

  const handleActionError = (error: unknown, fallbackMessage: string) => {
    setActionError(error instanceof Error ? error.message : fallbackMessage);
  };

  const getActionTestFlag = (row?: InvoiceRow | null) =>
    row?.isCmpInvoiceModuleTestData || shouldMarkCmpInvoiceModuleTestData || undefined;

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuInvoice(null);
  };

  const handleDownloadPdf = async (row: InvoiceRow) => {
    try {
      setActionError(null);
      const result = await downloadCmpInvoicePdf({
        variables: {
          input: {
            chamberId,
            invoiceId: row.id,
            isCmpInvoiceModuleTestData: getActionTestFlag(row),
          },
        },
      });
      const download = result.data?.downloadCmpInvoicePdf.download;

      if (!download) {
        throw new Error("Invoice PDF download did not return a file.");
      }

      downloadBase64File(download);
      setLastAction(`${row.invoiceNumber} PDF generated and downloaded.`);
      await refetch();
    } catch (error) {
      handleActionError(error, "Invoice PDF could not be generated.");
    }
  };

  const handleSendEmail = async (row: InvoiceRow) => {
    try {
      setActionError(null);
      const shouldResend = canUseAction(row, CmpInvoiceActionTypeEnum.ResendPaymentRequest);
      const variables = {
        input: {
          chamberId,
          invoiceId: row.id,
          isCmpInvoiceModuleTestData: getActionTestFlag(row),
        },
      };
      const payload = shouldResend
        ? (await resendCmpInvoicePaymentRequest({ variables })).data
            ?.resendCmpInvoicePaymentRequest
        : (await sendCmpInvoicePaymentRequest({ variables })).data
            ?.sendCmpInvoicePaymentRequest;
      const outboundEmail = payload?.outboundEmail;

      await refetch();

      if (!payload?.actionCompletedSuccessfully) {
        setActionError(
          `${row.invoiceNumber} email did not deliver${
            outboundEmail ? ` (status: ${outboundEmail.status}, to: ${outboundEmail.toEmail})` : ""
          }.`
        );
        return;
      }

      setLastAction(
        `${row.invoiceNumber} ${shouldResend ? "resent" : "sent"} to ${outboundEmail?.toEmail}. Status: ${outboundEmail?.status}.`
      );
      setSendSuccessMessage(
        shouldResend
          ? "Your invoice has been resent successfully!"
          : "Your invoice has been sent successfully!"
      );
    } catch (error) {
      handleActionError(error, "Invoice payment request could not be sent.");
    }
  };

  const handleApprove = async (row: InvoiceRow, sendPaymentRequest: boolean) => {
    try {
      setActionError(null);
      const result = await approveCmpInvoice({
        variables: {
          input: {
            chamberId,
            invoiceId: row.id,
            sendPaymentRequest,
            isCmpInvoiceModuleTestData: getActionTestFlag(row),
          },
        },
      });
      const payload = result.data?.approveCmpInvoice;
      const outboundEmail = payload?.outboundEmail;

      setPreviewDialogInvoice(null);
      await refetch();

      if (sendPaymentRequest && !payload?.actionCompletedSuccessfully) {
        setActionError(
          `${row.invoiceNumber} was approved, but the email did not deliver${
            outboundEmail ? ` (status: ${outboundEmail.status})` : ""
          }. Resend it from the Actions menu.`
        );
        return;
      }

      setLastAction(
        sendPaymentRequest
          ? `${row.invoiceNumber} approved and emailed to ${outboundEmail?.toEmail}. Status: ${outboundEmail?.status}.`
          : `${row.invoiceNumber} approved and saved as awaiting payment.`
      );
      if (sendPaymentRequest) {
        setSendSuccessMessage(
          "Your invoice has been approved and sent successfully!"
        );
      }
    } catch (error) {
      handleActionError(error, "Invoice could not be approved.");
    }
  };

  const handleSendReceipt = async (row: InvoiceRow) => {
    try {
      setActionError(null);
      const result = await resendCmpInvoiceReceipt({
        variables: {
          input: {
            chamberId,
            invoiceId: row.id,
            isCmpInvoiceModuleTestData: getActionTestFlag(row),
          },
        },
      });
      const payload = result.data?.resendCmpInvoiceReceipt;
      const outboundEmail = payload?.outboundEmail;

      await refetch();

      if (!payload?.actionCompletedSuccessfully) {
        setActionError(
          `${row.invoiceNumber} receipt did not deliver${
            outboundEmail ? ` (status: ${outboundEmail.status})` : ""
          }.`
        );
        return;
      }

      setLastAction(
        `${row.invoiceNumber} receipt sent to ${outboundEmail?.toEmail}. Status: ${outboundEmail?.status}.`
      );
    } catch (error) {
      handleActionError(error, "Invoice receipt could not be sent.");
    }
  };

  const handleConfirmDanger = async () => {
    if (!dangerDialog) {
      return;
    }

    const { invoice, kind } = dangerDialog;

    try {
      setActionError(null);

      if (kind === "reverse") {
        const result = await recordCmpInvoiceManualRefund({
          variables: {
            input: {
              chamberId,
              invoiceId: invoice.id,
              paymentMethodLabel:
                invoice.paymentMethodLabel && invoice.paymentMethodLabel !== "-"
                  ? invoice.paymentMethodLabel
                  : "Manual Reversal",
              reason: "Reversed from Manage Invoices",
              isCmpInvoiceModuleTestData: getActionTestFlag(invoice),
            },
          },
        });

        if (!result.data?.recordCmpInvoiceManualRefund.actionCompletedSuccessfully) {
          throw new Error("The paid invoice could not be reversed.");
        }

        setLastAction(
          `${invoice.invoiceNumber} reversed for ${invoice.customerName}.`
        );
      } else if (kind === "cancel") {
        const result = await voidCmpInvoice({
          variables: {
            input: {
              chamberId,
              invoiceId: invoice.id,
              reason: "Cancelled from Manage Invoices",
              isCmpInvoiceModuleTestData: getActionTestFlag(invoice),
            },
          },
        });

        if (!result.data?.voidCmpInvoice.actionCompletedSuccessfully) {
          throw new Error("The unpaid invoice could not be cancelled.");
        }

        setLastAction(
          `${invoice.invoiceNumber} cancelled for ${invoice.customerName}.`
        );
      } else {
        const result = await deleteCmpInvoiceDraft({
          variables: {
            input: {
              chamberId,
              invoiceId: invoice.id,
              isCmpInvoiceModuleTestData: getActionTestFlag(invoice),
            },
          },
        });

        if (!result.data?.deleteCmpInvoiceDraft.actionCompletedSuccessfully) {
          throw new Error("The draft invoice could not be deleted.");
        }

        setLastAction(`Draft invoice ${invoice.invoiceNumber} deleted.`);
      }

      setDangerDialog(null);

      if (previewDialogInvoice?.id === invoice.id) {
        setPreviewDialogInvoice(null);
      }

      await refetch();
    } catch (error) {
      handleActionError(error, "The invoice action could not be completed.");
    }
  };

  const handleExportInvoices = async ({
    displayStates,
    label,
    overdueBuckets,
    profile = CmpInvoiceAccountingExportProfileEnum.GenericCsv,
    search,
    sourceFilter,
    useCurrentFilters = true,
  }: {
    displayStates?: CmpInvoiceDisplayStateEnum[];
    label: string;
    overdueBuckets?: CmpInvoiceOverdueBucketEnum[];
    profile?: CmpInvoiceAccountingExportProfileEnum;
    search?: string;
    sourceFilter?: CmpInvoiceSourceFilterEnum;
    useCurrentFilters?: boolean;
  }) => {
    try {
      setActionError(null);
      const result = await exportCmpInvoicesAccountingCsv({
        variables: {
          input: {
            chamberId,
            profile,
            search: useCurrentFilters
              ? searchTerm || undefined
              : search || undefined,
            sourceFilter:
              useCurrentFilters
                ? typeFilter === "all" ? undefined : typeFilter
                : sourceFilter,
            displayStates:
              displayStates === undefined && useCurrentFilters
                ? getDisplayStateFilters(statusFilter)
                : displayStates,
            overdueBuckets:
              overdueBuckets === undefined && useCurrentFilters
                ? getOverdueBucketFilters(statusFilter)
                : overdueBuckets,
            sortColumn: sortColumnMap[sortColumn],
            sortDirection:
              sortDirection === "asc"
                ? CmpSortDirectionEnum.Asc
                : CmpSortDirectionEnum.Desc,
            isCmpInvoiceModuleTestData: shouldMarkCmpInvoiceModuleTestData || undefined,
          },
        },
      });
      const exportResult = result.data?.exportCmpInvoicesAccountingCsv.export;

      if (!exportResult) {
        throw new Error("Invoice export did not return a CSV file.");
      }

      downloadTextFile({
        content: exportResult.csv,
        contentType: exportResult.contentType,
        filename: exportResult.filename,
      });
      setLastAction(
        `${label} downloaded with ${exportResult.rowCount} row(s) as ${getExportProfileLabel(profile)}.`
      );
      await refetch();
    } catch (error) {
      handleActionError(error, "Invoice export could not be generated.");
    }
  };

  const handleStartCardCheckout = async () => {
    if (!paymentDialogInvoice) {
      return false;
    }

    try {
      setActionError(null);
      const result = await startCmpInvoiceCardCheckout({
        variables: {
          input: {
            chamberId,
            invoiceId: paymentDialogInvoice.id,
            installmentId: getNextInstallmentId(paymentDialogInvoice),
            successUrl: getStripeReturnUrl(),
            cancelUrl: getStripeReturnUrl(),
            isCmpInvoiceModuleTestData: getActionTestFlag(paymentDialogInvoice),
          },
        },
      });
      const checkoutUrl =
        result.data?.startCmpInvoiceCardCheckout.checkoutUrl;

      if (!checkoutUrl) {
        throw new Error("Stripe checkout did not return a URL.");
      }

      window.location.assign(checkoutUrl);
      return true;
    } catch (error) {
      handleActionError(error, "Stripe checkout could not be opened.");
      return false;
    }
  };

  const handleRecordManualPayment = async ({
    amountInCents,
    note,
    paymentMethodLabel,
    receivedAtUtcMilli,
    reference,
  }: RecordInvoicePaymentInput) => {
    if (!paymentDialogInvoice) {
      return false;
    }

    try {
      setActionError(null);
      const result = await recordCmpInvoiceManualPayment({
        variables: {
          input: {
            chamberId,
            invoiceId: paymentDialogInvoice.id,
            installmentId: getNextInstallmentId(paymentDialogInvoice),
            amountInCents,
            paymentMethodLabel,
            receivedAtUtcMilli,
            reference,
            note,
            isCmpInvoiceModuleTestData: getActionTestFlag(paymentDialogInvoice),
          },
        },
      });
      const saved = result.data?.recordCmpInvoiceManualPayment.actionCompletedSuccessfully;

      if (!saved) {
        throw new Error("Payment was not recorded.");
      }

      setLastAction(`${paymentDialogInvoice.invoiceNumber} payment was recorded.`);
      await refetch();
      await refetchInvoiceDetail();
      return true;
    } catch (error) {
      handleActionError(error, "Manual payment could not be recorded.");
      return false;
    }
  };

  // isHidden encodes the former front-end render predicates exactly;
  // isDisabled encodes the former disabled semantics. Email Invoice / Print
  // Invoice PDF deliberately stay rendered-but-disabled on voided/refunded
  // rows (Q-032 default: disable, not hide).
  const getInvoiceRowActions = buildRowActions<
    InvoiceMenuStatus,
    InvoiceRowActionKey,
    InvoiceRow
  >({
    matrix: invoiceRowActionMatrix,
    labels: invoiceRowActionLabels,
    getStatus: getInvoiceMenuStatus,
    destructiveActions: invoiceDestructiveRowActions,
    dividerBeforeActions: invoiceDestructiveRowActions,
    getIcon: (action) => {
      const ActionIcon = invoiceRowActionIcons[action];

      return <ActionIcon />;
    },
    isHidden: (action, row) => {
      if (action === "editDraft") {
        return !canEditCustomInvoice(row);
      }

      if (action === "applyPayment") {
        return !canApplyPaymentToInvoice(row);
      }

      if (action === "reversePaid") {
        return !canReversePaidInvoice(row);
      }

      if (action === "cancelUnpaid") {
        return !canCancelUnpaidInvoice(row);
      }

      if (action === "deleteDraft") {
        return !isDraftInvoice(row);
      }

      return false;
    },
    isDisabled: (action, row) => {
      if (action === "printPdf") {
        return (
          !canUseAction(row, CmpInvoiceActionTypeEnum.DownloadPdf) ||
          actionLoading
        );
      }

      if (action === "emailInvoice") {
        return (
          !(
            canUseAction(row, CmpInvoiceActionTypeEnum.SendPaymentRequest) ||
            canUseAction(row, CmpInvoiceActionTypeEnum.ResendPaymentRequest)
          ) || actionLoading
        );
      }

      if (action === "deleteDraft") {
        // Q-032: Delete Draft now disable-gates on the backend availability
        // flag like every other mutating action (it previously only checked
        // isDraftInvoice). Zero visible change while the backend grants
        // DELETE_DRAFT for drafts.
        return (
          !canUseAction(row, CmpInvoiceActionTypeEnum.DeleteDraft) ||
          actionLoading
        );
      }

      if (
        action === "applyPayment" ||
        action === "reversePaid" ||
        action === "cancelUnpaid"
      ) {
        return actionLoading;
      }

      return false;
    },
  });

  // RowActionMenu closes the menu first, then calls this — matching the
  // former closeActionMenu()-then-act ordering of the per-row menu items.
  const handleInvoiceRowAction = (
    action: InvoiceRowActionKey,
    row: InvoiceRow
  ) => {
    switch (action) {
      case "preview":
        setPreviewDialogInvoice(row);
        break;
      case "editDraft":
        navigate(getUpdatePath(row.id));
        break;
      case "printPdf":
        void handleDownloadPdf(row);
        break;
      case "applyPayment":
        setPaymentDialogInvoice(row);
        break;
      case "emailInvoice":
        void handleSendEmail(row);
        break;
      case "reversePaid":
        setDangerDialog({ invoice: row, kind: "reverse" });
        break;
      case "cancelUnpaid":
        setDangerDialog({ invoice: row, kind: "cancel" });
        break;
      case "deleteDraft":
      default:
        setDangerDialog({ invoice: row, kind: "delete" });
        break;
    }
  };

  const resetFilters = () => {
    setInvoiceNumberSearchQuery("");
    setMemberSearchQuery("");
    setTypeFilter("all");
    setStatusFilter("all");
    setInvoiceDateFromFilter("");
    setInvoiceDateToFilter("");
    setPaymentMethodFilter("all");
  };

  // INV-MANAGE-003: per-button active state drives the gold border + badge on
  // each of the four filter pills.
  const typeFilterIsActive = typeFilter !== "all";
  const dateFilterIsActive =
    invoiceDateFromFilter.length > 0 || invoiceDateToFilter.length > 0;
  const statusFilterIsActive = statusFilter !== "all";
  const paymentMethodFilterIsActive = paymentMethodFilter !== "all";

  const hasActiveInvoiceFilters =
    invoiceNumberSearchQuery.trim().length > 0 ||
    memberSearchQuery.trim().length > 0 ||
    typeFilterIsActive ||
    statusFilterIsActive ||
    dateFilterIsActive ||
    paymentMethodFilterIsActive;

  const overdueAmountInCents = useMemo(
    () =>
      analyticsRows.reduce(
        (total, row) =>
          row.daysPastDue && row.daysPastDue > 0
            ? total + row.balanceDueInCents
            : total,
        0
      ),
    [analyticsRows]
  );

  const agingBuckets = useMemo(
    () => {
      const bucketValues = {
        current: 0,
        oneToThirty: 0,
        thirtyOneToSixty: 0,
        sixtyOneToNinety: 0,
        ninetyPlus: 0,
      };

      agingRows.forEach((row) => {
        const daysPastDue = row.daysPastDue || 0;
        const balance = row.balanceDueInCents || 0;

        if (daysPastDue <= 0) {
          bucketValues.current += balance;
        } else if (daysPastDue <= 30) {
          bucketValues.oneToThirty += balance;
        } else if (daysPastDue <= 60) {
          bucketValues.thirtyOneToSixty += balance;
        } else if (daysPastDue <= 90) {
          bucketValues.sixtyOneToNinety += balance;
        } else {
          bucketValues.ninetyPlus += balance;
        }
      });

      return bucketValues;
    },
    [agingRows]
  );

  const analyticsCards = [
    {
      color: "#2563EB",
      helper: "All invoices generated",
      label: "Total Invoiced Revenue",
      value: formatCurrency(summary?.totalInvoicedAmountInCents),
    },
    {
      // Paid-revenue KPI green — part of the screenshot-derived local KPI
      // palette (with #2563EB/#EA580C/#DC2626); deliberately NOT
      // commonStyles.liveGreen (the Manage Events "Live"-status token, kept
      // distinct per UI_SYSTEM_CONTRACT) — see B4 audit.
      color: "#16A34A",
      helper: "Revenue collected",
      label: "Total Paid Revenue",
      value: formatCurrency(summary?.totalPaidAmountInCents),
    },
    {
      color: "#EA580C",
      helper: "Unpaid invoice balances",
      label: "Total Outstanding Revenue",
      value: formatCurrency(summary?.totalAmountDueInCents),
    },
    {
      color: "#DC2626",
      helper: "Past due invoice balances",
      label: "Total Overdue Revenue",
      value: formatCurrency(overdueAmountInCents),
    },
  ];

  const renderAnalyticsCard = ({
    color,
    helper,
    label,
    value,
  }: {
    color: string;
    helper: string;
    label: string;
    value: string;
  }) => (
    <article className="min-h-[145px] rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="text-[15px] font-extrabold leading-5 text-black">
        {label}
      </h3>
      <p className="mt-3 text-[28px] font-extrabold leading-tight" style={{ color }}>
        {value}
      </p>
      <p className="mt-2 text-sm font-medium leading-5 text-gray-900">{helper}</p>
    </article>
  );

  const agingBucketCards = [
    { label: "CURRENT", value: agingBuckets.current, highlight: true },
    { label: "1 - 30 DAYS", value: agingBuckets.oneToThirty },
    { label: "31 - 60 DAYS", value: agingBuckets.thirtyOneToSixty },
    { label: "61 - 90 DAYS", value: agingBuckets.sixtyOneToNinety },
    { label: "90+ DAYS", value: agingBuckets.ninetyPlus },
  ];
  const exportButtonConfigs = [
    {
      label: "Export All Invoices",
      onClick: () =>
        handleExportInvoices({
          label: "All invoices",
          useCurrentFilters: false,
        }),
    },
    {
      label: "Export Outstanding Invoices",
      onClick: () =>
        handleExportInvoices({
          displayStates: [CmpInvoiceDisplayStateEnum.NotPaid],
          label: "Outstanding invoices",
          useCurrentFilters: false,
        }),
    },
    {
      label: "Export Overdue Invoices",
      onClick: () =>
        handleExportInvoices({
          label: "Overdue invoices",
          overdueBuckets: [
            CmpInvoiceOverdueBucketEnum.OverdueUnder_60,
            CmpInvoiceOverdueBucketEnum.Overdue_60_89,
            CmpInvoiceOverdueBucketEnum.Overdue_90_119,
            CmpInvoiceOverdueBucketEnum.Overdue_120Plus,
          ],
          useCurrentFilters: false,
        }),
    },
    {
      label: "Export Credit Card Payments",
      onClick: () =>
        handleExportInvoices({
          label: "Credit card payments",
          useCurrentFilters: false,
        }),
    },
  ];
  const dangerDialogConfig = dangerDialog
    ? getDangerDialogConfig(dangerDialog.kind)
    : null;
  const renderPeriodToggle = ({
    onChange,
    value,
  }: {
    onChange: (period: ReportingPeriod) => void;
    value: ReportingPeriod;
  }) => (
    <div className="inline-flex rounded-md border border-gray-300 bg-white p-1">
      {[
        { label: "Year to Date", value: "yearToDate" as ReportingPeriod },
        { label: "Monthly", value: "monthly" as ReportingPeriod },
      ].map((option) => {
        const selected = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`inline-flex min-h-[42px] items-center gap-2 rounded px-4 text-sm font-extrabold ${
              selected
                ? "bg-cpaGold text-white"
                : "bg-white text-gray-600 hover:text-gray-900"
            }`}
          >
            <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
            {option.label}
          </button>
        );
      })}
    </div>
  );

  // Shared-DataTable column config: keys/labels/widths and cell markup are the
  // page's former header/body JSX moved verbatim. The leading checkbox column
  // stays DECORATIVE (uncontrolled, selects nothing) and rides as a regular
  // column via DataTableColumn.header's ReactNode widening — DataTable's
  // selection prop is deliberately NOT used (its padding="checkbox" geometry,
  // medium checkbox size and controlled wiring would change visuals/behavior).
  const invoiceColumns: DataTableColumn<InvoiceRow, InvoiceTableColumnKey>[] = [
    {
      key: "select",
      header: (
        <Checkbox
          size="small"
          sx={{
            color: "#9CA3AF",
            p: 0,
            // Checked checkboxes are CMP gold platform-wide (Q-008).
            "&.Mui-checked": { color: cpaGold },
          }}
        />
      ),
      width: "4%",
      cellSx: tableCellSx,
      render: () => (
        <Checkbox
          size="small"
          sx={{
            color: "#9CA3AF",
            p: 0,
            // Checked checkboxes are CMP gold platform-wide (Q-008).
            "&.Mui-checked": { color: cpaGold },
          }}
        />
      ),
    },
    {
      key: "invoiceNumber",
      header: "Invoice #",
      sortable: true,
      width: "9%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => (
        <button
          className="text-left text-[15px] font-extrabold text-[#C88B16] hover:text-[#A97412]"
          type="button"
          onClick={() => setPreviewDialogInvoice(row)}
        >
          {row.invoiceNumber}
        </button>
      ),
    },
    {
      key: "customerName",
      header: "Member Name",
      sortable: true,
      width: "13%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => (
        <div className="text-[15px] font-medium leading-6 text-gray-700">
          {row.customerName}
        </div>
      ),
    },
    {
      key: "type",
      header: "Invoice Type",
      sortable: true,
      width: "9%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => (
        <span className="text-[15px] font-medium leading-6 text-gray-600">
          {row.sourceLabel}
        </span>
      ),
    },
    {
      key: "issueDate",
      header: "Invoice Date",
      sortable: true,
      width: "9%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => formatDate(row.issuedAtUtcMilli),
    },
    {
      key: "dueDate",
      header: "Due Date",
      sortable: true,
      width: "9%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => formatDate(row.dueAtUtcMilli),
    },
    {
      key: "amount",
      header: "Amount",
      sortable: true,
      width: "8%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => (
        <span className="text-[15px] font-extrabold text-gray-900">
          {formatCurrency(row.totalAmountInCents)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance Owing",
      sortable: true,
      width: "9%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => (
        <span className="text-[15px] font-extrabold text-gray-900">
          {getBalanceOwingDisplay(row)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Invoice Status",
      sortable: true,
      width: "10%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => {
        const statusCopy = getRowStatusCopy(row);

        return (
          <StatusBadge
            variant="text"
            label={statusCopy.primary}
            textClassName={`text-[14px] font-extrabold ${statusCopy.className}`}
            secondaryLabel={statusCopy.secondary || undefined}
          />
        );
      },
    },
    {
      key: "paymentMethodLabel",
      header: "Billing Method",
      sortable: true,
      width: "9%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      render: (row) => (
        <div className="space-y-1 text-[14px] font-semibold leading-5 text-gray-700">
          {getBillingMethodLines(row).map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: "11%",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      stickyRight: true,
      render: (row) => (
        <RowActionsTrigger
          sx={tableActionButtonSx}
          onOpen={(anchorEl) => {
            setActionMenuAnchor(anchorEl);
            setActionMenuInvoice(row);
          }}
        />
      ),
    },
  ];

  return (
    // The shell's single statusMessage slot cannot host this page's five
    // independent Alerts, so they stay as leading children (identical DOM).
    <ManagePageShell
      className="w-full px-4 pb-10 sm:px-0"
      headerOverride={manageInvoicesHeaderOverride}
    >
      {stripeStatus === "payment-canceled" ? (
        <Alert severity="info" className="mb-5">
          Stripe checkout was canceled. The invoice is still unpaid unless a webhook later confirms a successful payment.
        </Alert>
      ) : null}

      {stripeStatus === "payment-processing" ? (
        <Alert severity="warning" className="mb-5">
          Stripe returned from checkout. CMP will mark the invoice paid only after the successful Stripe webhook is received.
        </Alert>
      ) : null}

      {lastAction ? (
        <Alert severity="success" className="mb-5" onClose={() => setLastAction(null)}>
          {lastAction}
        </Alert>
      ) : null}

      {actionError ? (
        <Alert severity="error" className="mb-5" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {manageInvoicesError ? (
        <Alert severity="error" className="mb-5">
          Invoice records could not be loaded: {manageInvoicesError.message}
        </Alert>
      ) : null}

      <section className="-mx-4 border-t border-gray-200 pt-8 sm:-mx-8 sm:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-[22px] font-extrabold leading-tight text-gray-900">
              Invoice Analytics
            </h2>
            <p className="mt-2 text-[15px] leading-6 text-gray-500">
              View invoice performance based on the selected reporting period.
            </p>
          </div>
          {renderPeriodToggle({
            onChange: setAnalyticsPeriod,
            value: analyticsPeriod,
          })}
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {analyticsCards.map((card) => (
            <React.Fragment key={card.label}>
              {renderAnalyticsCard(card)}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="mt-10 sm:-mx-8 sm:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-[22px] font-extrabold leading-tight text-gray-900">
              Accounts Receivable Aging Summary
            </h2>
            <p className="mt-2 text-[15px] leading-6 text-gray-500">
              View outstanding invoices.
            </p>
          </div>
          {renderPeriodToggle({
            onChange: setAgingPeriod,
            value: agingPeriod,
          })}
        </div>
        <div className="mt-7 rounded-xl border border-gray-200 bg-white px-6 py-8 shadow-sm">
          <div className="grid grid-cols-1 gap-y-7 md:grid-cols-5 md:divide-x md:divide-gray-100">
            {agingBucketCards.map((bucket) => (
              <div key={bucket.label} className="text-center">
                <div className="text-[13px] font-extrabold uppercase tracking-normal text-black">
                  {bucket.label}
                </div>
                <div
                  className="mt-4 text-[24px] font-extrabold leading-tight"
                  style={{ color: bucket.highlight ? "#D29421" : "#111827" }}
                >
                  {formatCurrency(bucket.value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-10 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:mx-0 sm:p-8">
        <h2 className="text-[20px] font-extrabold leading-tight text-gray-900">
          Invoice Filters
        </h2>
        <p className="mt-3 text-[15px] leading-6 text-black">
          Filter invoices by member, invoice status, invoice type, date range, payment method, or invoice number.
        </p>
        {/* FilterSearchField className is cleared to "" — its default
            min-w/flex-basis classes would fight this page's explicit CSS grid
            tracks at xl widths. fieldSx passes the page's minHeight-56 variant
            (shared default is height-56). */}
        <FilterBar className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-[280px_minmax(320px,1fr)_220px] xl:grid-cols-[280px_minmax(520px,1fr)_220px]">
          <FilterSearchField
            value={invoiceNumberSearchQuery}
            onChange={setInvoiceNumberSearchQuery}
            placeholder="Filter by Invoice #"
            className=""
            adornmentIcon={<TuneOutlinedIcon sx={{ color: "#8F969E" }} />}
            fieldSx={filterFieldSx}
          />
          <FilterSearchField
            value={memberSearchQuery}
            onChange={setMemberSearchQuery}
            placeholder="Search by member name or details"
            className=""
            fieldSx={filterFieldSx}
          />
          <ClearFiltersButton
            onClick={resetFilters}
            disabled={!hasActiveInvoiceFilters}
          />
        </FilterBar>
        {/* INV-MANAGE-003 active treatment now comes from the shared
            activeFilterButtonSx (byte-identical); the idle-only sx override
            keeps this page's lighter #8F969E label without touching it. */}
        <FilterBar className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <FilterMenuButton
            label="Filter by Invoice Type"
            onOpen={setTypeMenuAnchor}
            active={typeFilterIsActive}
            badgeCount={typeFilterIsActive ? 1 : null}
            badgeAriaLabel="1 invoice type selected"
            sx={typeFilterIsActive ? undefined : idleFilterButtonLabelSx}
          />
          <FilterMenuButton
            label={getInvoiceDateFilterLabel({
              invoiceDateFromFilter,
              invoiceDateToFilter,
            })}
            onOpen={setDateMenuAnchor}
            active={dateFilterIsActive}
            startIcon={<CalendarTodayOutlinedIcon />}
            sx={dateFilterIsActive ? undefined : idleFilterButtonLabelSx}
          />
          <FilterMenuButton
            label="Filter by Invoice Status"
            onOpen={setStatusMenuAnchor}
            active={statusFilterIsActive}
            badgeCount={statusFilterIsActive ? 1 : null}
            badgeAriaLabel="1 status selected"
            sx={statusFilterIsActive ? undefined : idleFilterButtonLabelSx}
          />
          <FilterMenuButton
            label="Filter by Billing Method"
            onOpen={setPaymentMenuAnchor}
            active={paymentMethodFilterIsActive}
            badgeCount={paymentMethodFilterIsActive ? 1 : null}
            badgeAriaLabel="1 billing method selected"
            sx={
              paymentMethodFilterIsActive ? undefined : idleFilterButtonLabelSx
            }
          />
        </FilterBar>
      </section>

      <Menu
        anchorEl={typeMenuAnchor}
        open={Boolean(typeMenuAnchor)}
        onClose={() => setTypeMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 260, py: 1 } }}
      >
        {invoiceTypeFilterOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={typeFilter === option.value}
            onClick={() => {
              setTypeFilter(option.value);
              setTypeMenuAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => setDateMenuAnchor(null)}
        PaperProps={{ sx: dateRangeMenuPaperSx }}
      >
        <div className="space-y-3">
          <div>
            <div className="text-sm font-extrabold leading-5 text-gray-900">
              Invoice date range
            </div>
            <div className="mt-0.5 text-xs font-medium leading-5 text-gray-500">
              Select one date or a from/to range.
            </div>
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">From</p>
            <CalendarDateInput
              ariaLabel="Invoice date from"
              mode="fullDate"
              placeholder="Select Date"
              iconPosition="right"
              value={invoiceDateFromFilter}
              onChange={setInvoiceDateFromFilter}
            />
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">To</p>
            <CalendarDateInput
              ariaLabel="Invoice date to"
              mode="fullDate"
              placeholder="Select Date"
              iconPosition="right"
              value={invoiceDateToFilter}
              onChange={setInvoiceDateToFilter}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              disabled={!invoiceDateFromFilter && !invoiceDateToFilter}
              onClick={() => {
                setInvoiceDateFromFilter("");
                setInvoiceDateToFilter("");
              }}
              size="small"
              sx={{
                color: "#4B5563",
                fontSize: 13,
                fontWeight: 800,
                textTransform: "none",
              }}
              type="button"
            >
              Clear dates
            </Button>
            <Button
              onClick={() => setDateMenuAnchor(null)}
              size="small"
              sx={{
                backgroundColor: cpaGold,
                color: "#fff",
                fontSize: 13,
                fontWeight: 800,
                px: 2,
                textTransform: "none",
                "&:hover": { backgroundColor: cpaGoldDark },
              }}
              type="button"
              variant="contained"
            >
              Done
            </Button>
          </div>
        </div>
      </Menu>
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 260, py: 1 } }}
      >
        {statusFilterOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={statusFilter === option.value}
            onClick={() => {
              setStatusFilter(option.value);
              setStatusMenuAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={paymentMenuAnchor}
        open={Boolean(paymentMenuAnchor)}
        onClose={() => setPaymentMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 280, py: 1 } }}
      >
        {paymentMethodOptions.map((option) => (
          <MenuItem
            key={option}
            selected={paymentMethodFilter === option}
            onClick={() => {
              setPaymentMethodFilter(option);
              setPaymentMenuAnchor(null);
            }}
          >
            {option === "all" ? "All billing methods" : option}
          </MenuItem>
        ))}
      </Menu>

      <section className="mt-12">
        <div>
          <h2 className="text-[24px] font-extrabold leading-tight text-gray-900">
            Invoices
          </h2>
          <p className="mt-2 text-[15px] leading-6 text-gray-500">
            View and manage all invoice details
          </p>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          {exportButtonConfigs.map((button) => (
            <Button
              key={button.label}
              type="button"
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => void button.onClick()}
              disabled={!chamberId || exportingCsv}
              sx={exportButtonSx}
            >
              {button.label}
            </Button>
          ))}
        </div>
        <DataTable
          ariaLabel="manage invoices table"
          columns={invoiceColumns}
          rows={visibleRows}
          getRowKey={(row) => row.id}
          sort={{
            column: sortColumn,
            direction: sortDirection,
            // Only sortable columns render sort labels, so the shared table
            // never requests a sort on the "select"/"actions" keys.
            onRequestSort: (column) => handleRequestSort(column as ColumnKey),
          }}
          // Both gates deliberately track the FILTERED rows (unlike Manage
          // Members' unfiltered loading gate) — the page's exact former
          // expressions.
          showLoadingRow={manageInvoicesLoading && visibleRows.length === 0}
          loadingRowContent={
            <div className="flex items-center justify-center gap-3 py-10 text-sm font-semibold text-gray-500">
              <CircularProgress size={20} />
              Loading invoice records...
            </div>
          }
          showEmptyRow={!manageInvoicesLoading && visibleRows.length === 0}
          emptyRowContent={
            <div className="py-10 text-center text-sm font-semibold text-gray-500">
              No invoices match the current filters.
            </div>
          }
          containerClassName="mt-10 w-full overflow-x-auto rounded-xl border border-gray-200 bg-white"
          tableSx={invoiceTableSx}
          // This page's TableHead is classless — the head background comes
          // from the per-cell #F8FAFC in invoiceTableSx, so the shared
          // bg-gray-50 default must be cleared.
          headClassName=""
          rowClassName="hover:bg-gray-50"
        />
      </section>

      {/* Single page-level action menu (RowActionMenu usage contract) —
          replaces the former per-row <Menu> instances with identical items,
          order, single divider and styling. */}
      <RowActionMenu
        actions={
          actionMenuInvoice ? getInvoiceRowActions(actionMenuInvoice) : []
        }
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor && actionMenuInvoice)}
        onClose={closeActionMenu}
        onAction={(action) => {
          if (actionMenuInvoice) {
            handleInvoiceRowAction(action, actionMenuInvoice);
          }
        }}
        paperSx={actionMenuPaperSx}
        itemSx={actionMenuItemSx}
        destructiveItemSx={destructiveMenuItemSx}
      />

      <InvoicePreviewModal
        actionLoading={actionLoading}
        canApplyPayment={
          !!previewDialogInvoice && canApplyPaymentToInvoice(previewDialogInvoice)
        }
        canPrintPdf={
          !!previewDialogInvoice &&
          canUseAction(previewDialogInvoice, CmpInvoiceActionTypeEnum.DownloadPdf)
        }
        canSendInvoice={
          !!previewDialogInvoice &&
          (canUseAction(
            previewDialogInvoice,
            CmpInvoiceActionTypeEnum.SendPaymentRequest
          ) ||
            canUseAction(
              previewDialogInvoice,
              CmpInvoiceActionTypeEnum.ResendPaymentRequest
            ))
        }
        sendIsResend={
          !!previewDialogInvoice &&
          canUseAction(
            previewDialogInvoice,
            CmpInvoiceActionTypeEnum.ResendPaymentRequest
          )
        }
        chamber={chamber}
        detail={
          selectedActionInvoice?.id === previewDialogInvoice?.id
            ? selectedInvoiceDetail
            : null
        }
        errorMessage={invoiceDetailError?.message || null}
        loading={invoiceDetailLoading}
        onAddPayment={() => {
          if (!previewDialogInvoice) {
            return;
          }

          setPaymentDialogInvoice(previewDialogInvoice);
          setPreviewDialogInvoice(null);
        }}
        onApproveSave={() => {
          if (previewDialogInvoice) {
            void handleApprove(previewDialogInvoice, false);
          }
        }}
        onApproveSend={() => {
          if (previewDialogInvoice) {
            void handleApprove(previewDialogInvoice, true);
          }
        }}
        onClose={() => setPreviewDialogInvoice(null)}
        onPrintPdf={() => {
          if (previewDialogInvoice) {
            void handleDownloadPdf(previewDialogInvoice);
          }
        }}
        onSendInvoice={() => {
          if (previewDialogInvoice) {
            void handleSendEmail(previewDialogInvoice);
          }
        }}
        onSendReceipt={() => {
          if (previewDialogInvoice) {
            void handleSendReceipt(previewDialogInvoice);
          }
        }}
        open={!!previewDialogInvoice}
        row={previewDialogInvoice}
      />

      <InvoiceSuccessDialog
        message={sendSuccessMessage || ""}
        onClose={() => setSendSuccessMessage(null)}
        open={!!sendSuccessMessage}
      />

      <ProcessPaymentModal
        actionError={actionError || invoiceDetailError?.message || null}
        detail={
          selectedActionInvoice?.id === paymentDialogInvoice?.id
            ? selectedInvoiceDetail
            : null
        }
        loading={invoiceDetailLoading}
        onClose={() => setPaymentDialogInvoice(null)}
        onRecordManualPayment={handleRecordManualPayment}
        onStartCardCheckout={handleStartCardCheckout}
        open={!!paymentDialogInvoice}
        recordingManualPayment={recordingManualPayment}
        row={paymentDialogInvoice}
        startingCardCheckout={startingCardCheckout}
      />

      <Dialog
        open={!!dangerDialog}
        onClose={() => setDangerDialog(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "22px",
            maxWidth: 620,
            position: "relative",
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
          },
        }}
      >
        {dangerDialog && dangerDialogConfig ? (
          <>
            <DialogTitle className="p-0 text-center">
              <IconButton
                aria-label="Close invoice action confirmation"
                onClick={() => setDangerDialog(null)}
                sx={{
                  backgroundColor: "#F31723",
                  color: "white",
                  height: 38,
                  position: "absolute",
                  right: 22,
                  top: 18,
                  width: 38,
                  zIndex: 1,
                  "&:hover": { backgroundColor: "#D90F1A" },
                }}
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-[#F31723] text-white">
                <dangerDialogConfig.Icon sx={{ fontSize: 62 }} />
              </div>
              <span className="mt-8 block text-[28px] font-extrabold leading-tight text-black">
                {dangerDialogConfig.title}
              </span>
            </DialogTitle>
            <DialogContent className="px-0 pt-5">
              <p className="mx-auto max-w-[500px] text-center text-[19px] leading-8 text-black">
                Are you sure you want to {dangerDialogConfig.verb} for{" "}
                {dangerDialog.invoice.customerName}?
              </p>
            </DialogContent>
            <DialogActions
              className="px-0 pb-0 pt-6"
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                width: "100%",
                "& > :not(style) ~ :not(style)": {
                  marginLeft: 0,
                },
              }}
            >
              <Button
                variant="contained"
                disabled={actionLoading}
                startIcon={
                  dangerDialog.kind === "delete" ? (
                    <DeleteOutlineIcon sx={{ fontSize: 30 }} />
                  ) : undefined
                }
                onClick={() => void handleConfirmDanger()}
                sx={{
                  backgroundColor: "#F31723",
                  borderRadius: "10px",
                  boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
                  color: "white",
                  fontSize: 18,
                  fontWeight: 800,
                  height: 58,
                  minWidth: 210,
                  textTransform: "uppercase",
                  "& .MuiButton-startIcon": { mr: 1.5 },
                  "&:hover": { backgroundColor: "#D90F1A" },
                }}
              >
                {dangerDialogConfig.actionLabel}
              </Button>
              <Button
                variant="contained"
                onClick={() => setDangerDialog(null)}
                sx={{
                  backgroundColor: commonStyles.charcoal,
                  borderRadius: "10px",
                  boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
                  color: "white",
                  fontSize: 18,
                  fontWeight: 800,
                  height: 58,
                  minWidth: 210,
                  textTransform: "uppercase",
                  "&:hover": { backgroundColor: "#111827" },
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </ManagePageShell>
  );
};

export default ManageInvoices;
